import { fnv1a } from './trace-utils';

/**
 * Redator de PII — redige NA ORIGEM, antes de o payload sair do dispositivo.
 *
 * O funil do pedido carrega PII pesada (nome, telefone, CPF, endereço completo,
 * lat/lng da casa, dados de cartão). A telemetria precisa da FORMA e do
 * DINHEIRO — que é o que os testes da Etapa 2 vão afirmar —, nunca da pessoa.
 * Então o dado pessoal vira tipo+comprimento e o valor monetário passa cru.
 *
 * DEFAULT-DENY: o que não está na allowlist do step é DESCARTADO, não redigido.
 * Um campo novo no app não vaza por esquecimento — vaza um `_dropped` no evento,
 * que é justamente o sinal de que alguém precisa atualizar a allowlist. A regra
 * tem dentes: adicionar campo ao `data` exige adicionar à allowlist.
 *
 * O padrão de allowlist declarativa não é invenção: `order.service.ts:57`
 * (`orderItemsProperties` + `removeTrashFromProduct`) já poda o payload do
 * pedido exatamente assim.
 *
 * Este arquivo é código de dinheiro e tem spec próprio: um redator com bug
 * envenena TODOS os goldens da Etapa 2 a jusante.
 */

/** Como um campo permitido é transformado antes de sair do dispositivo. */
export type Shape =
  | 'raw'        // verbatim — enums, ids, flags, nome de produto
  | 'money'      // Number, 2 casas — o objeto do teste
  | 'num'        // Number — contadores, distâncias
  | 'bool'       // !!v
  | 'str'        // 'str(<len>)' — telefone, nome, observação
  | 'coord3'     // 3 casas (~110m) — mantém a zona, não acha a casa
  | 'redacted'   // 'redacted' — cartão, cvv, token, CPF
  | 'hash8';     // fnv1a 8 hex — compara sem revelar (cupom, e-mail)

/** Descritor recursivo: shape, lista homogênea, ou objeto aninhado. */
export type FieldSpec = Shape | { $each: FieldSpec } | { [key: string]: FieldSpec };

export interface RedactResult {
  value: any;
  /** Quantas chaves foram descartadas por não estarem na allowlist. */
  dropped: number;
}

const SHAPES = ['raw', 'money', 'num', 'bool', 'str', 'coord3', 'redacted', 'hash8'];

function isShape(spec: any): spec is Shape {
  return typeof spec === 'string' && SHAPES.indexOf(spec) !== -1;
}

/** Dinheiro com 2 casas, sem a poeira de float (0.1+0.2 = 0.30000000000000004). */
function toMoney(v: any): number | null {
  const n = Number(v);
  if (!isFinite(n)) return null;
  return Math.round(n * 100) / 100;
}

function applyShape(value: any, shape: Shape): any {
  if (value === undefined) return undefined;

  switch (shape) {
    case 'raw':
      return value;

    case 'money':
      return value === null ? null : toMoney(value);

    case 'num': {
      if (value === null) return null;
      const n = Number(value);
      return isFinite(n) ? n : null;
    }

    case 'bool':
      return value === null ? null : !!value;

    case 'str': {
      if (value === null) return null;
      // Comprimento, não conteúdo: 'str(11)' diz ao teste que veio um telefone
      // brasileiro sem dizer de quem.
      const s = String(value);
      return 'str(' + s.length + ')';
    }

    case 'coord3': {
      if (value === null) return null;
      const n = Number(value);
      // 3 casas ≈ 110 m: preserva a zona de entrega (que decide a taxa) e não
      // identifica a residência.
      return isFinite(n) ? Number(n.toFixed(3)) : null;
    }

    case 'redacted':
      return value === null ? null : 'redacted';

    case 'hash8': {
      if (value === null) return null;
      // Permite GROUP BY / comparação de igualdade sem revelar o valor.
      return ('00000000' + fnv1a(String(value)).toString(16)).slice(-8);
    }

    default:
      return undefined;
  }
}

function redactValue(value: any, spec: FieldSpec, counter: { dropped: number }): any {
  if (isShape(spec)) return applyShape(value, spec);

  // Lista homogênea: { $each: <spec> }
  if (spec && typeof spec === 'object' && '$each' in (spec as any)) {
    if (!Array.isArray(value)) return value === undefined ? undefined : null;
    const each = (spec as any).$each as FieldSpec;
    return value.map((item) => redactValue(item, each, counter));
  }

  // Objeto aninhado: default-deny nas chaves.
  if (spec && typeof spec === 'object') {
    if (value === null || value === undefined) return value === undefined ? undefined : null;
    if (typeof value !== 'object' || Array.isArray(value)) return null;

    const allow = spec as { [k: string]: FieldSpec };
    const out: any = {};
    for (const key of Object.keys(value)) {
      if (!(key in allow)) {
        // O campo existe no app e ninguém o declarou: descartado e contado.
        counter.dropped++;
        continue;
      }
      const redacted = redactValue(value[key], allow[key], counter);
      if (redacted !== undefined) out[key] = redacted;
    }
    return out;
  }

  return undefined;
}

/**
 * Redige `data` contra a allowlist do step.
 *
 * Step sem allowlist declarada devolve `{}` com tudo contado como dropped — o
 * default-deny vale também para o step: emitir um step novo sem declará-lo não
 * vaza nada, só acende o `_dropped`.
 */
export function redact(data: any, spec: FieldSpec | undefined): RedactResult {
  const counter = { dropped: 0 };

  if (!spec) {
    const dropped = data && typeof data === 'object' ? Object.keys(data).length : 0;
    return { value: {}, dropped };
  }

  try {
    // `data` ausente (null/undefined) não é payload a redigir: vira objeto
    // vazio. Emitir `data: null` deixaria o Athena com um caso a mais para
    // tratar em toda query, sem informação nenhuma em troca.
    const input = data === undefined || data === null ? {} : data;
    const value = redactValue(input, spec, counter);
    return { value: value === undefined || value === null ? {} : value, dropped: counter.dropped };
  } catch (e) {
    // Redator nunca derruba o emitter, e emitter nunca derruba o checkout.
    // Em caso de payload patológico (circular, getter que lança), não emitir
    // conteúdo é melhor que emitir PII não redigida.
    return { value: {}, dropped: -1 };
  }
}
